import {
	formatLiqTxPopupFields,
	type LiqTxPopupFields,
} from "@/lib/agent-tx-detail";
import { X } from "lucide-react";
import { createPortal } from "react-dom";

export type LiqTxDetailSource = {
	kind: string;
	vaultId: number | null;
	ts: number;
	detail: string;
	usdmSpentE8: number;
};

function formatTs(ts: number): string {
	if (!Number.isFinite(ts) || ts <= 0) return "—";
	const ms = ts > 1e15 ? ts / 1e6 : ts;
	try {
		return new Date(ms).toLocaleString();
	} catch {
		return "—";
	}
}

export function LiqTxDetailDialog({
	tx,
	onClose,
}: {
	tx: LiqTxDetailSource | null;
	onClose: () => void;
}) {
	if (!tx || typeof document === "undefined") return null;

	const fields: LiqTxPopupFields = formatLiqTxPopupFields(
		tx.detail,
		tx.usdmSpentE8,
	);

	return createPortal(
		<div
			className="fixed inset-0 z-[120] flex items-center justify-center p-4"
			onClick={onClose}
		>
			<div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
			<div
				role="dialog"
				aria-modal
				aria-labelledby="liq-tx-detail-title"
				className="relative w-full max-w-md rounded-2xl border border-yellow-500/40 bg-black p-5 shadow-xl"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex items-start justify-between gap-3">
					<div>
						<p className="font-mono text-[10px] font-bold uppercase tracking-wider text-yellow-400/70">
							Bot transaction
						</p>
						<h3
							id="liq-tx-detail-title"
							className="font-mono text-lg font-bold text-yellow-300"
						>
							{tx.kind}
							{tx.vaultId != null ? ` · vault ${tx.vaultId}` : ""}
						</h3>
						<p className="mt-0.5 font-mono text-[11px] text-yellow-400/50">
							{formatTs(tx.ts)}
						</p>
					</div>
					<button
						type="button"
						className="rounded-lg border border-yellow-500/40 p-1.5 text-yellow-400/70 hover:text-yellow-300"
						aria-label="Close"
						onClick={onClose}
					>
						<X className="size-4" />
					</button>
				</div>

				<dl className="mt-4 space-y-3 font-mono text-sm">
					<div className="grid grid-cols-2 gap-2">
						<div className="rounded-lg border border-yellow-500/25 px-3 py-2">
							<dt className="text-[10px] text-yellow-400/50">Paid (repay)</dt>
							<dd className="font-bold text-yellow-200">
								{fields.paidUsdm != null ? `${fields.paidUsdm} USDM` : "—"}
							</dd>
						</div>
						<div className="rounded-lg border border-yellow-500/25 px-3 py-2">
							<dt className="text-[10px] text-yellow-400/50">Commission</dt>
							<dd className="font-bold text-yellow-200">
								{fields.commissionUsdm != null
									? `${fields.commissionUsdm} USDM`
									: "—"}
							</dd>
						</div>
						<div className="rounded-lg border border-yellow-500/25 px-3 py-2">
							<dt className="text-[10px] text-yellow-400/50">Total spent</dt>
							<dd className="font-bold text-yellow-200">
								{fields.totalSpentUsdm != null
									? `${fields.totalSpentUsdm} USDM`
									: "—"}
							</dd>
						</div>
						<div className="rounded-lg border border-emerald-500/30 px-3 py-2">
							<dt className="text-[10px] text-emerald-400/60">
								Profit at tx time
							</dt>
							<dd className="font-bold text-emerald-300">
								{fields.profitUsdm != null
									? `~${fields.profitUsdm} USDM`
									: "—"}
							</dd>
						</div>
					</div>

					<div>
						<dt className="text-[10px] uppercase text-yellow-400/50">
							Collateral seized
						</dt>
						<dd className="mt-1">
							{fields.assets.length > 0 ? (
								<ul className="space-y-1">
									{fields.assets.map((a) => (
										<li
											key={a.token}
											className="flex justify-between rounded-md border border-yellow-500/20 px-3 py-1.5 text-yellow-100"
										>
											<span>{a.token}</span>
											<span className="tabular-nums">{a.amountLabel}</span>
										</li>
									))}
								</ul>
							) : (
								<p className="text-yellow-400/55">{fields.assetsLabel}</p>
							)}
						</dd>
					</div>

					{fields.bonusBps != null ? (
						<p className="text-[10px] text-yellow-400/45">
							Bonus {fields.bonusBps} bps
							{fields.legacy
								? " · profit not recorded on this older row"
								: ""}
						</p>
					) : null}
				</dl>

				<button
					type="button"
					className="mt-5 w-full rounded-md bg-yellow-400 py-2.5 font-mono text-sm font-bold text-black hover:bg-yellow-300"
					onClick={onClose}
				>
					Close
				</button>
			</div>
		</div>,
		document.body,
	);
}
